/**
 * useCommandSources — builds the ⌘K command palette's result set.
 *
 * Three sources, one ranked/grouped output:
 *  - Pages   → flattened from the shared role-aware `useOpsNav` (single source
 *              of truth with the sidebar; role gating inherited for free).
 *  - Records → clients/drivers/applicants via the existing `/v1/portal/search`,
 *              debounced with a stale-response guard.
 *  - Actions → a couple of static UI actions (theme, sidebar) from the UI store.
 *
 * The palette component owns `query` (v-model'd to the input); this composable
 * reacts to it and exposes ranked `groups` + a flattened list for keyboard nav.
 */
import { ref, computed, watch, onScopeDispose } from 'vue'
import { api, useUIStore, useOpsNav, type NavItem } from '@claimshield/shared'

export type CommandKind = 'page' | 'record' | 'action'
export type RecordType = 'Client' | 'Driver' | 'Applicant'

// The search endpoint returns these today; anything else is dropped defensively
// so a future server-side type can't throw in the grouping/routing paths.
const KNOWN_RECORD_TYPES: readonly RecordType[] = ['Client', 'Driver', 'Applicant']

export interface Command {
  id: string
  kind: CommandKind
  label: string
  sub?: string
  icon: string // 'pi pi-*' for pages/actions; '' for records (rendered as a badge)
  route?: string
  query?: Record<string, string>
  run?: () => void
  recordType?: RecordType
  keywords?: string
}

export interface CommandGroup {
  heading: string
  items: Command[]
}

interface RawRecord {
  type: RecordType
  id: string
  label: string
  sub?: string
  client_id?: string | null
}

const RECORD_MIN_CHARS = 2
const DEBOUNCE_MS = 200

/**
 * Subsequence scorer. Returns null on no match; higher is better.
 * Prefix > word-boundary substring > mid-word substring > subsequence.
 */
function score(query: string, text: string): number | null {
  const q = query.toLowerCase()
  const t = text.toLowerCase()
  if (!q) return 0

  const idx = t.indexOf(q)
  if (idx === 0) return 1000
  if (idx > 0) {
    const boundary = /[\s/\-·›]/.test(t[idx - 1] ?? '')
    return (boundary ? 800 : 500) - idx
  }

  // Fuzzy subsequence — every query char appears in order.
  let ti = 0
  let streak = 0
  let acc = 0
  for (const ch of q) {
    let found = false
    while (ti < t.length) {
      if (t[ti] === ch) {
        streak++
        acc += 1 + streak
        ti++
        found = true
        break
      }
      streak = 0
      ti++
    }
    if (!found) return null
  }
  return 100 + acc
}

/** Best score of the query against a command's label, then label+sub+keywords. */
function matchScore(cmd: Command, q: string): number | null {
  const primary = score(q, cmd.label)
  if (primary !== null) return primary
  const hay = [cmd.sub, cmd.keywords].filter(Boolean).join(' ')
  return hay ? score(q, hay) : null
}

/** Mirror OpsLayout.handleSearchSelect routing so records land identically. */
function buildRecordQuery(r: RawRecord): Record<string, string> {
  if (r.type === 'Client') return { client: r.id }
  const q: Record<string, string> = {}
  if (r.client_id) q.client = r.client_id
  q.highlight = r.id
  return q
}

export function useCommandSources() {
  const uiStore = useUIStore()
  const { navSections } = useOpsNav()

  const query = ref('')
  const records = ref<Command[]>([])
  const recordsLoading = ref(false)
  const recordsError = ref(false)

  // Pages grouped by their sidebar section (nav order preserved).
  const pageGroups = computed<CommandGroup[]>(() => {
    const groups: CommandGroup[] = []
    for (const section of navSections.value) {
      const items: Command[] = []
      const walk = (navItems: NavItem[]) => {
        for (const it of navItems) {
          if (it.route) {
            items.push({
              id: `page:${it.route}:${it.label}`,
              kind: 'page',
              label: it.label,
              sub: section.title || undefined,
              icon: it.icon,
              route: it.route,
            })
          }
          if (it.children) walk(it.children)
        }
      }
      walk(section.items)
      if (items.length) groups.push({ heading: section.title || 'Navigate', items })
    }
    return groups
  })

  const pageCommands = computed<Command[]>(() => pageGroups.value.flatMap((g) => g.items))

  const actionCommands = computed<Command[]>(() => [
    {
      id: 'action:theme',
      kind: 'action',
      label: uiStore.isDarkMode ? 'Switch to light mode' : 'Switch to dark mode',
      icon: uiStore.isDarkMode ? 'pi pi-sun' : 'pi pi-moon',
      keywords: 'theme dark light mode appearance',
      run: () => uiStore.setTheme(uiStore.isDarkMode ? 'light' : 'dark'),
    },
    {
      id: 'action:sidebar',
      kind: 'action',
      label: uiStore.sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar',
      icon: uiStore.sidebarCollapsed ? 'pi pi-angle-right' : 'pi pi-angle-left',
      keywords: 'sidebar collapse expand navigation',
      run: () => uiStore.toggleSidebar(),
    },
  ])

  // Debounced record search with a stale-response guard: a slow earlier response
  // can never clobber a newer query's results.
  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  watch(query, (val) => {
    const q = val.trim()
    if (debounceTimer) clearTimeout(debounceTimer)

    if (q.length < RECORD_MIN_CHARS) {
      records.value = []
      recordsLoading.value = false
      recordsError.value = false
      return
    }

    recordsLoading.value = true
    recordsError.value = false
    debounceTimer = setTimeout(async () => {
      const issuedFor = q
      try {
        const res = await api.get('/v1/portal/search', { params: { q } })
        if (query.value.trim() !== issuedFor) return // stale — a newer query is in flight
        const raw: RawRecord[] = res.data?.data ?? []
        records.value = raw
          .filter((r) => KNOWN_RECORD_TYPES.includes(r.type))
          .map((r) => ({
            id: `record:${r.type}:${r.id}`,
            kind: 'record' as const,
            label: r.label,
            sub: r.sub || undefined,
            icon: '',
            recordType: r.type,
            query: buildRecordQuery(r),
          }))
      } catch {
        if (query.value.trim() !== issuedFor) return
        records.value = []
        recordsError.value = true
      } finally {
        if (query.value.trim() === issuedFor) recordsLoading.value = false
      }
    }, DEBOUNCE_MS)
  })

  const groups = computed<CommandGroup[]>(() => {
    const q = query.value.trim()

    // Empty query → browse mode: all pages by section + actions.
    if (!q) {
      return [...pageGroups.value, { heading: 'Actions', items: actionCommands.value }]
    }

    const rank = (cmds: Command[]): Command[] =>
      cmds
        .map((c) => ({ c, s: matchScore(c, q) }))
        .filter((x): x is { c: Command; s: number } => x.s !== null)
        .sort((a, b) => b.s - a.s)
        .map((x) => x.c)

    const out: CommandGroup[] = []
    const pages = rank(pageCommands.value)
    if (pages.length) out.push({ heading: 'Pages', items: pages })
    const actions = rank(actionCommands.value)
    if (actions.length) out.push({ heading: 'Actions', items: actions })

    // Records already match server-side; group by type in a stable order.
    const byType: Record<RecordType, Command[]> = { Client: [], Driver: [], Applicant: [] }
    for (const r of records.value) {
      if (r.recordType) byType[r.recordType].push(r)
    }
    if (byType.Client.length) out.push({ heading: 'Clients', items: byType.Client })
    if (byType.Driver.length) out.push({ heading: 'Drivers', items: byType.Driver })
    if (byType.Applicant.length) out.push({ heading: 'Applicants', items: byType.Applicant })

    return out
  })

  const flatItems = computed<Command[]>(() => groups.value.flatMap((g) => g.items))

  function reset(): void {
    query.value = ''
    records.value = []
    recordsLoading.value = false
    recordsError.value = false
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
  }

  // Never let a pending debounce fire after the palette component is torn down
  // (e.g. logout unmounts it) — that would be a wasted request writing to a
  // disposed scope.
  onScopeDispose(() => {
    if (debounceTimer) clearTimeout(debounceTimer)
  })

  return { query, groups, flatItems, recordsLoading, recordsError, reset }
}
